import { registerInitialState } from '@modules/authentication/constants';
import {
  LockRounded,
  MailRounded,
  PersonRounded,
  VisibilityOffRounded,
  VisibilityRounded,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';
import { ChangeEvent, useCallback, useState } from 'react';
import { useToggle } from 'react-use';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import useSignUp from 'v2/core/services/auth/useSignUp';
import { getAuthErrorMessage } from 'v2/core/services/auth/utils';
import { RegisterState } from 'v2/views/layouts/authenticationModule/authTypes';
import { ComplianceLinks } from './ComplianceLinks';

interface RegisterViewProps {
  onSetUserPayload: (payload: RegisterState, otpView: boolean) => void;
  setAuthErrorMessage: (errorMessage: string) => void;
  onSsoError: () => void;
}

export const RegisterView = ({
  onSetUserPayload,
  setAuthErrorMessage,
  onSsoError,
}: RegisterViewProps) => {
  const [showPassword, toggleShowPassword] = useToggle(false);
  const [state, setState] = useState<RegisterState>(registerInitialState);

  const handleOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const {
        target: { value, name },
      } = e;
      setState({
        ...state,
        [name]: value,
      });
    },
    [state],
  );

  const { firstName, lastName, email, password } = state;

  const [signUp, status] = useSignUp({
    payload: state,
    onSuccess: onSetUserPayload,
    onSsoError,
    onError: (err) => {
      const errorMessage = getAuthErrorMessage(err);
      setAuthErrorMessage(errorMessage);
    },
  });

  return (
    <Box sx={{ width: '100%' }} component="form" onSubmit={signUp}>
      <Stack spacing={3}>
        <Stack direction="row" spacing={1}>
          <TextField
            label="First Name"
            placeholder="Enter first name"
            onChange={handleOnChange}
            type="text"
            name="firstName"
            value={firstName}
            required
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonRounded color="disabled" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Last Name"
            placeholder="Enter last name"
            onChange={handleOnChange}
            type="text"
            name="lastName"
            value={lastName}
            required
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonRounded color="disabled" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
        <TextField
          label="Email Address"
          placeholder="Enter email address"
          onChange={handleOnChange}
          type="email"
          name="email"
          value={email}
          required
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MailRounded color="disabled" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Password"
          placeholder="Enter your password"
          onChange={handleOnChange}
          type={showPassword ? 'text' : 'password'}
          name="password"
          value={password}
          required
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockRounded color="disabled" />
              </InputAdornment>
            ),
            endAdornment: password?.length ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={toggleShowPassword}
                  onMouseDown={toggleShowPassword}
                >
                  {showPassword ? (
                    <VisibilityRounded />
                  ) : (
                    <VisibilityOffRounded />
                  )}
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <ComplianceLinks />
        <LoadingButton
          fullWidth
          size="large"
          variant="contained"
          type="submit"
          loading={IsLoading(status)}
          disabled={IsLoading(status)}
          sx={{ mb: 2 }}
        >
          Sign Up
        </LoadingButton>
      </Stack>
    </Box>
  );
};
